<!--
  Main Application Template
  ========================
  
  This template defines the root layout structure for the MacSafety web application.
  It includes the main navigation menu, header toolbar, and content area with
  responsive design patterns and conditional rendering based on user state.
  
  Layout Structure:
  - Side Navigation Menu: Overlay menu with user authentication and form navigation
  - Main Content Area: Router outlet for dynamic content with responsive header
  - Development Utilities: Conditional Ionic component preloading for development mode
  
  Responsive Design:
  - Desktop: Logo in header start, title in center, auth buttons in end
  - Mobile: Condensed header with menu button and responsive title
  - Adaptive: Different logo images and layouts based on screen size
  
  Authentication States:
  - Logged Out: Login button and minimal navigation
  - Logged In: User info, form navigation, admin features (if applicable)
  
  Key Features:
  - Dynamic route title and icon display
  - Conditional admin-only features
  - Responsive logo and layout adaptation
  - Form navigation with dynamic routing
  - Safe area handling for mobile devices
  
  @author MacSafety Development Team
  @version 1.0
-->

<ion-app class="ion-safe-area-top">
  <!-- 
    Side Navigation Menu
    ====================
    Overlay menu that slides in from the left, containing user authentication,
    navigation links, and form access. Only visible when menu is opened.
  -->
  <ion-menu contentId="main-content" type="overlay">
    <!-- Menu Header with Logo -->
    <ion-header>
<ion-toolbar color="primary">

        <ion-title class="ion-text-center"  (click)="navigateToHome()">
      
          <!-- Desktop Logo: Full logo for large screens -->
          <img
            *ngIf="!isMobile() && isLargeScreen()"
            src="assets/logo.png"
            alt="MacSafety Logo"
            style="height: 36px"
          />
          <!-- Mobile/Tablet Logo: Compact logo for smaller screens -->
          <img
            *ngIf="isMobile() || !isLargeScreen()"
            src="assets/MacSafety-E-Forms.png"
            alt="MacSafety E-Forms Logo"
            style="height: 36px"
          />
        </ion-title>
      </ion-toolbar>
    </ion-header>

    <!-- Menu Content Area -->
    <ion-content>
      <ion-list class="ion-padding" lines="none">
        <!-- 
          Authentication State: Logged Out
          =================================
          Shows login button when user is not authenticated
        -->
        <ng-container *ngIf="!isUserLoggedIn">
          <ion-item button (click)="login()">
            <ion-icon name="log-in-outline" slot="start"></ion-icon>
            <ion-label>Login</ion-label>
          </ion-item>
        </ng-container>

        <!-- 
          Authentication State: Logged In
          ================================
          Shows user information and navigation when authenticated
        -->
        <ng-container *ngIf="isUserLoggedIn">
          <!-- User Information Display -->
          <ion-item lines="none" class="user-info">
            <ion-icon
              name="person-outline"
              slot="start"
              color="medium"
            ></ion-icon>
            <ion-label>
              <h3 class="ion-no-margin">{{ currentUserName }}</h3>
              <ion-note>Logged in</ion-note>
            </ion-label>
          </ion-item>

          <!-- 
            Admin-Only Features
            ===================
            Form entry viewing - only visible to admin users
          -->
          <ion-menu-toggle auto-hide="true">
            <ion-item
              button
              *ngIf="userIsAdmin"
              routerLink="/form-entries"
              routerDirection="root"
              routerLinkActive="selected"
            >
              <ion-icon name="list-outline" slot="start"></ion-icon>
              <ion-label class="italic-link">View Form Entries</ion-label>
            </ion-item>
          </ion-menu-toggle>

          <!-- 
            Main Forms Navigation
            ======================
            Link to forms landing page with visual indicator
          -->
          <ion-menu-toggle auto-hide="true">
            <ion-item
              button
              routerLink="/forms"
              routerDirection="root"
              class="menu-all-forms"
              routerLinkActive="selected"
            >
              <ion-icon name="apps-outline" slot="start"></ion-icon>
              <ion-label class="italic-link">All Forms</ion-label>
              <ion-icon name="chevron-down-outline" slot="end"></ion-icon>
            </ion-item>
          </ion-menu-toggle>

          <!-- 
            Individual Form Navigation
            ===========================
            Dynamic list of all available forms with icons and titles
            Generated from form metadata in the component
          -->
          <ion-menu-toggle auto-hide="true" *ngFor="let form of accessibleForms">
            <ion-item
              button
              [routerLink]="['/forms', form.formType]"
              routerDirection="root"
              routerLinkActive="selected"
              class="ion-padding-start"
            >
              <ion-icon [name]="form.icon" slot="start"></ion-icon>
              <ion-label>{{ form.title }}</ion-label>
            </ion-item>
          </ion-menu-toggle>

          <!-- 
            External Vehicle Collision Form Link
            ====================================
            Opens the external vehicle collision form in a new tab
        -->

          <ion-menu-toggle auto-hide="true">
  <ion-item
    button
    (click)="openVehicleCollisionForm()"
    class="ion-padding-start"
  >
    <ion-icon name="car-outline" slot="start"></ion-icon>
    <ion-label>Vehicle Collision Report</ion-label>
  </ion-item>
</ion-menu-toggle>
        </ng-container>
      </ion-list>
    </ion-content>

    <!-- 
      Pinned Footer: Logout Button
      =============================
      Always visible logout button when user is authenticated
      Positioned at bottom of menu for easy access
    -->
    <ion-footer >
      <ion-toolbar>
        <!-- Admin-Only: Bulk Resend Emails Modal -->
        <ion-menu-toggle auto-hide="true">
          <ion-item
            *ngIf="isUserLoggedIn && userIsAdmin"
            button
            (click)="openBulkResendModal()"
            lines="none"
          >
            <ion-icon name="send-outline" slot="start"></ion-icon>
            <ion-label class="italic-link">Bulk Resend Emails</ion-label>
          </ion-item>
        </ion-menu-toggle>

        <ion-item *ngIf="isUserLoggedIn" button (click)="logout()" lines="none">
          <ion-icon name="log-out-outline" slot="start"></ion-icon>
          <ion-label>Logout</ion-label>
        </ion-item>
   
      </ion-toolbar>
      <ion-item id="small-version-item" lines="none">
   
        {{appVersion}}
 
      </ion-item>
     
    </ion-footer>
   
  </ion-menu>

  <!-- 
    Main Application Content Area
    =============================
    Primary content area with header toolbar and router outlet
    Contains the main application interface and navigation
  -->
  <div class="ion-page" id="main-content">
    <!-- 
      Application Header Toolbar
      ==========================
      Responsive header with navigation, branding, and authentication
      Adapts layout based on screen size and device type
    -->
    <ion-header>
      <ion-toolbar class="app-toolbar">
        <!-- 
          Mobile Menu Button
          ===================
          Always visible on all screen sizes for menu access
        -->
        <ion-buttons slot="start">
          <ion-menu-button></ion-menu-button>
        </ion-buttons>
    
        <!-- 
          Desktop Layout: Logo and Title
          ===============================
          Full layout for larger screens with logo in start slot and title in center
        -->
        <ion-buttons slot="start" *ngIf="!isSmallScreen() && !isMobile()">
          <img
            (click)="navigateToHome()"
            src="assets/MacSafety-E-Forms.png"
            alt="MacSafety Logo"
            style="height: 40px; width: auto; cursor: pointer; margin-left: 40px;"
          />
        </ion-buttons>
        <ion-title *ngIf="!isSmallScreen() && !isMobile()" style="max-width: 50vw;">
          <div style="display: flex; align-items: center; justify-content: center; gap: 8px;">
            <!-- Dynamic Route Icon: Shows current route's associated icon -->
            <ion-icon *ngIf="currentRouteIcon()" [name]="currentRouteIcon()"></ion-icon>
            <!-- Dynamic Route Title: Shows current route's title with truncation -->
            <span style="
              white-space: nowrap; 
              overflow: hidden; 
              text-overflow: ellipsis;
              max-width: 600px;
            ">
              {{ currentTitle }}
            </span>
          </div>
        </ion-title>
    
        <!-- 
          Mobile Layout: Condensed Header
          =================================
          Compact layout for small screens and mobile devices
          Combines menu button, icon, and title in single row
        -->
        <ion-title *ngIf="isSmallScreen() || isMobile()" class="ion-no-padding">
          <div class="ion-flex " style="gap: 2px;">
            <!-- Dynamic Route Icon: Shows current route's associated icon -->
            <ion-icon *ngIf="currentRouteIcon()" [name]="currentRouteIcon()"></ion-icon>
            <!-- Responsive Title: Adapts font size based on viewport width -->
            <ion-text class="ion-text-nowrap ion-text-truncate" style="font-size: clamp(14px, 4vw, 18px);">
              {{ currentTitle }}
            </ion-text>
          </div>
        </ion-title>
    
        <!-- 
          Desktop Authentication Buttons
          ===============================
          Login/logout buttons positioned in header end slot for larger screens
          Only visible on desktop to avoid mobile header clutter
        -->
        <ng-container *ngIf="!isSmallScreen() && !isMobile()">
          <!-- Login Button: Shown when user is not authenticated -->
          <ion-buttons slot="end" *ngIf="!isUserLoggedIn">
            <ion-button (click)="login()">
              <ion-icon slot="start" name="log-in-outline"></ion-icon>
              Login
            </ion-button>
          </ion-buttons>
    
          <!-- User Info and Logout: Shown when user is authenticated -->
          <ion-buttons slot="end" *ngIf="isUserLoggedIn">
            <!-- User Display: Shows current user name (non-clickable) -->
            <ion-button
              fill="clear"
              (click)="$event.preventDefault(); $event.stopPropagation()"
              class="non-clickable-button"
            >
              <ion-icon name="person-outline"></ion-icon>
              {{ currentUserName }}
            </ion-button>
            <!-- Logout Button: Triggers user logout process -->
            <ion-button (click)="logout()">
              <ion-icon slot="start" name="log-out-outline"></ion-icon>
              Logout
            </ion-button>
          </ion-buttons>
        </ng-container>
      
      </ion-toolbar>
      
    </ion-header>

    <!-- 
      Main Content Router Outlet
      ===========================
      Dynamic content area that displays routed components
      Handles safe area for mobile devices with bottom padding
    -->
    <ion-content class="app-page-content ion-safe-area-bottom">
      <router-outlet></router-outlet>
    </ion-content>
  </div>

  <!-- 
    Development Mode: Ionic Component Preloader
    ===========================================
    Conditionally renders the LoadAllIonicComponent only in development mode
    This component preloads all Ionic components to prevent import loss issues
    during development. Not included in production builds.
  -->
  <app-load-all-ionic *ngIf="isDevMode"></app-load-all-ionic>
</ion-app>
